import { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import type { ExecutionResult, DocumentNode } from 'graphql';
import { type SubscriptionOptions } from 'apollo-client';

import { useSessionData } from '@confluence/session-data';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fg } from '@confluence/feature-gating';

import { getAGGClient } from '../../getAGGClient';

import { getConfluenceContentAri } from './getConfluenceContentAri';
import { wsEventsEmitter } from './WsEventsEmitter';

type Unsubscribe = () => void;

interface SubscribeSink<R> {
	next: (value: R) => void;
	error?: (error: Error) => void;
	complete?: () => void;
}
type FetchResult<T = Record<string, unknown>> = ExecutionResult & {
	subscriptionData?: {
		data?: T;
		extensions?: {
			sequenceNumber?: number;
			agg: {
				request_id: string;
			};
		};
	};
};
const subscribe = <Response = Record<string, unknown>>(
	payload: SubscriptionOptions,
	sink: SubscribeSink<Response>,
	onSubscriptionComplete: () => void,
	lastSequenceId: React.MutableRefObject<number>,
	skip: boolean,
	operationName: string,
	contentId?: string,
	contentType?: string,
): Unsubscribe | undefined => {
	const client = getAGGClient();
	const id = payload.variables?.id || '';
	if (!client || skip || !id) {
		return;
	}

	const { query, variables } = payload;
	const { next, error, complete = () => {} } = sink;
	let traceId: string;
	let request_id: string;

	const subscription = client.subscribe({
		query,
		variables,
	});

	const observer = {
		next: (result: FetchResult<Response>) => {
			const aggTraceId = result?.extensions?.traceId;
			const aggRequestId = result?.extensions?.agg?.request_id;
			if (aggTraceId) {
				traceId = aggTraceId;
			}
			if (aggRequestId) {
				request_id = aggRequestId;
			}
			getAnalyticsWebClient().then((analyticsClient) => {
				analyticsClient.sendOperationalEvent({
					action: 'received',
					actionSubject: 'subscription.event',
					actionSubjectId: operationName,
					source: 'useSubscribe',
					attributes: {
						contentId,
						contentType,
						traceId,
						request_id,
					},
				});
			});
			// update the last received message timestamp with either the messages time or use a best guess from the current time
			lastSequenceId.current =
				result?.subscriptionData?.extensions?.sequenceNumber ??
				Math.floor(new Date().getTime() / 1000);

			next(result as unknown as Response);
		},
		complete: () => {
			complete();
			onSubscriptionComplete();
		},
		error: (err: any) => {
			error?.(err);
		},
	};

	const subscriptionObserver = subscription.subscribe(observer);

	return () => subscriptionObserver?.unsubscribe?.();
};

type UseSubscribeProps = {
	contentId?: string;
	contentType?: string;
	query: DocumentNode;
	operationName: string;
	variables?: Record<string, any>;
	skip?: boolean;
	sink: SubscribeSink<any>;
	replayCap?: number;
};

/**
 * useSubscribe is a custom hook that subscribes to a GraphQL subscription using WebSocket.
 * It handles the connection, disconnection, and updates to the subscription.
 * It also manages the replay of events based on the last sequence ID.
 * MAKE SURE TO ONLY PASS IN MEMOIZED values to the hook
 * @param {string} contentId - The ID of the content to subscribe to.
 * @param {string} contentType - The type of the content to subscribe to.
 * @param {DocumentNode} query - The GraphQL query to subscribe to.
 * @param {string} operationName - The name of the subscription operation.
 * @param {Record<string, any>} variables - The variables to pass to the query.
 * @param {boolean} skip - Whether to skip the subscription.
 * @param {SubscribeSink<any>} sink - The sink to handle the subscription data.
 * @param {number} replayCap - The maximum date of messages to playback, if nothing is passed in the default is 24 hours ago, cannot be further back than 7 days.
 * @returns {void}
 */
export const useSubscribe = ({
	contentId,
	contentType,
	query,
	operationName,
	variables,
	skip = false,
	sink,
	replayCap,
}: UseSubscribeProps) => {
	const { activationId, cloudId } = useSessionData();
	const lastSequenceId = useRef(Math.floor(new Date().getTime() / 1000));
	const [updateExtensionsTrigger, setUpdateExtensionsTrigger] = useState(0);

	const ari =
		!contentId || !contentType || !cloudId || !activationId
			? ''
			: getConfluenceContentAri({
					siteId: cloudId,
					resourceType: contentType,
					activationId,
					resourceId: contentId,
				});
	const onSubscriptionComplete = useCallback(() => {
		setUpdateExtensionsTrigger(updateExtensionsTrigger + 1);
	}, [updateExtensionsTrigger, setUpdateExtensionsTrigger]);

	const extensions = useMemo(() => {
		const twentyFourHoursAgoInSecondsFallbackReplayCap = Math.floor(
			(Date.now() - 24 * 60 * 60 * 1000) / 1000,
		);
		let sequenceNumber;
		if (replayCap !== undefined) {
			sequenceNumber = replayCap;
		} else if (lastSequenceId.current < twentyFourHoursAgoInSecondsFallbackReplayCap) {
			sequenceNumber = twentyFourHoursAgoInSecondsFallbackReplayCap;
		} else {
			sequenceNumber = lastSequenceId.current;
		}

		return updateExtensionsTrigger > 0 && fg('confluence_enable_agg_subscription_link_replay')
			? {
					command: {
						type: 'replay',
						data: {
							sequenceNumber,
						},
					},
				}
			: {};
	}, [updateExtensionsTrigger, replayCap]);

	const updatedPayload = useMemo(() => {
		if (fg('confluence_enable_agg_subscription_link_replay')) {
			return {
				query,
				variables: {
					...variables,
					__extensions: extensions,
					id: ari,
				},
			};
		} else {
			return {
				query,
				variables: {
					...variables,
					id: ari,
				},
			};
		}
	}, [ari, extensions, variables, query]);

	useEffect(() => {
		const handleConnected = () => {
			if (fg('confluence_enable_agg_subscription_link_replay')) {
				setUpdateExtensionsTrigger((trigger) => trigger + 1);
			}
		};

		wsEventsEmitter.on('updateSubscription', handleConnected);

		return () => {
			wsEventsEmitter.off('updateSubscription', handleConnected);
		};
	}, [setUpdateExtensionsTrigger]);

	useEffect(() => {
		const unsubscribe: Unsubscribe | undefined = subscribe(
			updatedPayload,
			sink,
			onSubscriptionComplete,
			lastSequenceId,
			skip,
			operationName,
			contentId,
			contentType,
		);

		return () => {
			unsubscribe?.();
		};
	}, [updatedPayload, sink, onSubscriptionComplete, skip, contentId, contentType, operationName]);
};
